import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import useApi from 'hooks/useApi';
import { showAlert } from 'redux/actions/alertActions';

import MainSpinner from 'shared/components/MainSpinner';
import { LabelInput, LabelError } from 'shared/components/Labels';
import { Button } from 'shared/components/Buttons';
import { TextField, Switch } from 'shared/components/form/Inputs';
import TextArea from 'shared/components/textarea';
import DropdownSearch from 'shared/components/DropdownSearch';
import MultipleSelectSearch from 'shared/components/MultipleSelectSearch';
import InputDate from 'shared/components/inputdate/InputDate';
import { GroupFieldWrapper } from 'shared/components/SharedStyles/SharedStyles';
import FieldType from 'shared/helpers/fieldType';
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from 'shared/utils';
import { convertErrorsArrayToString } from 'shared/helpers/helper';
import { colors } from 'shared/defaultTheme';

import { ReactComponent as SpecialAreaIcon } from 'assets/icon-special-area.svg';

import { CustomDataFormWrapper, EmptyForm, CustomFieldText } from '../styles';

const CustomDataForm = ({ clientId, getActiveTab, setHasChangeCustomData, isSaveCustomData }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { customAreaApi } = useApi();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const formCustomDataRef = useRef();
  const [customFields, setCustomFields] = useState([]);
  const [createCustomFields, setCreateCustomFields] = useState(false);

  const [{ loading }, getCustomFields] = customAreaApi(
    {
      url: `${window.API_V1}/cli/fields/${auth.cusNavId}/${clientId}`,
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: loadingPost }, postCustomFields] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const fetchCustomFields = async () => {
    try {
      const {
        data: { data, errors: getErrors },
      } = await getCustomFields();
      if (getErrors.length) {
        return;
      }
      if (data) {
        const isNew = data.length === data.filter((customField) => !customField.value).length;
        setCreateCustomFields(isNew);
        setCustomFields(data.sort((a, b) => a.order - b.order).filter((x) => x.id));
        return;
      }
      setCustomFields([]);
    } catch (ex) {
      console.error('ex');
    }
  };

  useEffect(() => {
    if (clientId.length > 0) {
      fetchCustomFields();
    }
  }, [clientId]);

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  const selectedMultipleList = (members) => {
    let newMembersMultiple = [];
    members
      .filter((item) => item.type !== FieldType.GROUP && item.id)
      .forEach((member) => {
        newMembersMultiple = [
          ...newMembersMultiple,
          { name: getNameSelectedLanguage(member.i18List), id: member.id },
        ];
      });
    return newMembersMultiple;
  };

  const resetDefaultValues = useCallback(() => {
    customFields.forEach((customField) => {
      switch (customField.type) {
        case FieldType.MULTI_LIST:
          if (customField.value) {
            setValue(customField.id, customField.value);
          } else {
            setValue(customField.id, []);
          }
          break;
        case FieldType.OPTION:
          if (customField.value) {
            setValue(customField.id, customField.value[0] === 'true');
          } else {
            setValue(customField.id, '');
          }
          break;
        default:
          if (customField.value) {
            setValue(customField.id, customField.value[0]);
          } else {
            setValue(customField.id, '');
          }
      }
    });
  }, [customFields]);

  useEffect(() => {
    if (customFields.length) {
      if (!createCustomFields) {
        resetDefaultValues();
        return;
      }
      customFields.forEach((customField) => setValue(customField.id, ''));
    }
  }, [customFields]);

  const onSubmit = async (values) => {
    try {
      const url = createCustomFields
        ? `${window.API_V1}/cli/fields/${auth.cusNavId}/${clientId}/modify`
        : `${window.API_V1}/cli/fields/${auth.cusNavId}/${clientId}/modify`;
      const data = [];
      customFields.forEach((customField) =>
        data.push({
          ...customField,
          value: Array.isArray(values[customField.id])
            ? [...values[customField.id]]
            : [String(values[customField.id])],
        })
      );
      const {
        data: { errors: postErrors },
      } = await postCustomFields({
        url,
        data,
      });
      if (postErrors.length) {
        throw new Error(convertErrorsArrayToString(postErrors));
      }
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
      setHasChangeCustomData(false);
      setCreateCustomFields(false);
      setCustomFields(data);
    } catch (ex) {
      console.error(ex);
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
    }
  };

  useEffect(() => {
    if (isSaveCustomData) {
      formCustomDataRef.current.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      );
    }
  }, [isSaveCustomData]);

  const onCancel = () => {
    getActiveTab('1', t('client_management_basic_data'));
    if (!createCustomFields) {
      resetDefaultValues();
      return;
    }
    customFields.forEach((customField) => setValue(customField.id, ''));
  };

  const renderField = (customField, field) => {
    switch (customField.type) {
      case FieldType.TEXT:
        return (
          <TextField
            value={field.value ?? ''}
            onChange={(e) => {
              setHasChangeCustomData(true);
              field.onChange(e);
            }}
            onBlur={field.onBlur}
            error={errors[customField.id] != null}
          />
        );
      case FieldType.TEXT_AREA:
        return (
          <TextArea
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={errors[customField.id] != null}
          />
        );
      case FieldType.NUMBER:
      case FieldType.CURRENCY:
        return (
          <TextField
            type="number"
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={errors[customField.id] != null}
          />
        );
      case FieldType.LIST:
        return (
          <DropdownSearch
            id={`custom-dropdown-search-${customField.id}`}
            options={
              customField.members
                ? [...customField.members]
                    .sort((memberA, memberB) => memberA.order - memberB.order)
                    .map((member) => ({
                      name: getNameSelectedLanguage(member.i18List),
                      value: member.id,
                    }))
                : []
            }
            value={field.value}
            onBlur={field.onBlur}
            onChange={field.onChange}
            error={errors[customField.id] !== undefined}
          />
        );
      case FieldType.MULTI_LIST:
        return (
          <MultipleSelectSearch
            className="list-field"
            dataSource={selectedMultipleList(
              customField.members
                ? [...customField.members].sort((memberA, memberB) => memberA.order - memberB.order)
                : []
            )}
            fields={{ text: 'name', value: 'id' }}
            defaultValue={field.value.length ? field.value : undefined}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={errors[customField.id] !== undefined}
          />
        );
      case FieldType.OPTION:
        return (
          <Switch
            id={customField.id}
            label={getNameSelectedLanguage(customField.i18List)}
            color={field.value ? colors.pastelGreen : colors.venetianRed}
            value={field.value === '' ? true : !field.value}
            onChange={(e) => {
              setHasChangeCustomData(true);
              field.onChange(e);
            }}
          />
        );
      case FieldType.CALENDAR:
        return (
          <InputDate
            className="input-date"
            value={field.value ? moment(field.value).format('DD/MM/YYYY') : field.value}
            onChange={field.onChange}
            error={errors[customField.id] !== undefined}
          />
        );
      default:
        return <div />;
    }
  };

  if (loading || loadingPost) {
    return <MainSpinner />;
  }

  return (
    <CustomDataFormWrapper>
      <form
        className="form w-custom-data-form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        ref={formCustomDataRef}
      >
        <Row className="w-100">
          {customFields
            .filter((customField) => !customField.parentId)
            .map((customField) => (
              <React.Fragment key={customField.id}>
                <Col sm={12}>
                  <div className="form__form-group">
                    <LabelInput
                      className="form__form-group-label"
                      label={getNameSelectedLanguage(customField.i18List)}
                      color={colors.black}
                    />
                    {customField.type === FieldType.GROUP ? (
                      <GroupFieldWrapper>
                        {customFields
                          .filter(
                            (customFieldChild) => customFieldChild.parentId === customField.id
                          )
                          .map((customFieldChild) => (
                            <React.Fragment key={customFieldChild.id}>
                              <div className="form__form-group">
                                {customFieldChild.type !== FieldType.OPTION && (
                                  <LabelInput
                                    className="form__form-group-label"
                                    label={getNameSelectedLanguage(customFieldChild.i18List)}
                                    color={colors.black}
                                  />
                                )}
                                <Controller
                                  name={customFieldChild.id}
                                  control={control}
                                  defaultValue=""
                                  rules={
                                    customFieldChild.type !== FieldType.GROUP &&
                                    customFieldChild.size > 0
                                      ? {
                                          maxLength: {
                                            value: customFieldChild.size,
                                            message: t('general_max_length_field').replace(
                                              '{param}',
                                              customFieldChild.size
                                            ),
                                          },
                                        }
                                      : undefined
                                  }
                                  render={({ field }) => renderField(customFieldChild, field)}
                                />
                                {errors[customFieldChild.id] && (
                                  <LabelError label={errors[customFieldChild.id].message} />
                                )}
                              </div>
                            </React.Fragment>
                          ))}
                      </GroupFieldWrapper>
                    ) : (
                      <Controller
                        name={customField.id}
                        control={control}
                        defaultValue=""
                        rules={
                          customField.type !== FieldType.GROUP && customField.size > 0
                            ? {
                                maxLength: {
                                  value: customField.size,
                                  message: t('general_max_length_field').replace(
                                    '{param}',
                                    customField.size
                                  ),
                                },
                              }
                            : undefined
                        }
                        render={({ field }) => renderField(customField, field)}
                      />
                    )}
                    {errors[customField.id] && (
                      <LabelError label={errors[customField.id].message} />
                    )}
                  </div>
                </Col>
              </React.Fragment>
            ))}
        </Row>
        <Row className="w-100">
          {customFields.length > 0 ? (
            <Col className="d-flex flex-row justify-content-end" sm={12}>
              <Button
                className="mr-3"
                type="button"
                color="secondary"
                label={t('general_button_cancel')}
                onClick={onCancel}
              />
              <Button
                type="submit"
                color="primary"
                label={t('general_button_save')}
                disabled={Object.keys(errors).length >= 1}
              />
            </Col>
          ) : (
            <EmptyForm>
              <SpecialAreaIcon />
              <CustomFieldText>{t('custom_field_not_found_custom_fields')}</CustomFieldText>
              <CustomFieldText className="subText">
                {t('custom_field_not_found_subtext_custom_fields')}
              </CustomFieldText>
            </EmptyForm>
          )}
        </Row>
      </form>
    </CustomDataFormWrapper>
  );
};

CustomDataForm.defaultProps = {
  getActiveTab: undefined,
  setHasChangeCustomData: null,
  isSaveCustomData: false,
};

CustomDataForm.propTypes = {
  clientId: PropTypes.string.isRequired,
  getActiveTab: PropTypes.func,
  setHasChangeCustomData: PropTypes.func,
  isSaveCustomData: PropTypes.bool,
};

export default CustomDataForm;
